import React, { useMemo } from 'react';
import { Plus, X } from 'lucide-react';
import { Text } from '@cotera/client/app/components/ui';
import { Button } from '@cotera/client/v0/components/ui/button';
import { useDatasetShape } from '@cotera/client/app/v1/trigger/hooks';
import { cn } from '@cotera/client/v0/lib/utils';
import { TokenInput } from '../tiptap/token-input';
import type { ToolInputRefIssue } from '../tool-input-ref-issues';
import { RefIssueNotice } from '../ref-issue-notice';
import { TOOL_INPUT_CONTROL_HEIGHT } from '../tool-input-styles';
import type { ToolInputControlProps } from './index';
import {
  ConditionOps,
  DATASET_FILTER_OP_OPTIONS,
  datasetFilterOpTakesValue,
  parseConditions,
  serializeConditions,
  type DatasetCondition,
  type DatasetFilterOp,
} from './dataset-conditions-model';

const SELECT_CLASS = cn(
  TOOL_INPUT_CONTROL_HEIGHT,
  'rounded-md border border-input bg-background px-2 text-sm'
);

/**
 * Builds the `where` of a dataset query as rows of `column op value` — the
 * column chosen from the selected dataset's own columns, the operator from a
 * fixed list, and the value typed literally or pulled from an earlier step with
 * a `{{ref}}` token (so a filter can read "the id from the trigger").
 *
 * Reads `datasetId` from its siblings to offer the right columns, the same way
 * the column picker and write builder do; until a dataset is chosen there is
 * nothing to filter on, so it says so rather than offering a blank row.
 */
export const DatasetConditionsControl: React.FC<ToolInputControlProps> = ({
  field,
  params,
  sources,
  refIssues,
  setValues,
}) => {
  const rawDatasetId = params['datasetId'];
  const datasetId =
    typeof rawDatasetId === 'string' && rawDatasetId !== ''
      ? rawDatasetId
      : null;
  const shape = useDatasetShape(datasetId);
  const columns = shape?.columns ?? [];

  const conditions = useMemo(
    () => parseConditions(params[field.name]),
    [params, field.name]
  );

  const commit = (next: readonly DatasetCondition[]): void => {
    setValues({ [field.name]: serializeConditions(next) });
  };

  // Cell issues are reported against `<field>.<index>.value`; group them by row.
  const issuesByRow = useMemo(() => {
    const re = new RegExp(`^${field.name}\\.(\\d+)\\.value$`);
    const byRow = new Map<number, ToolInputRefIssue[]>();
    for (const issue of refIssues) {
      const match = re.exec(issue.path);
      const index = match === null ? -1 : Number(match[1]);
      if (index < 0) {
        continue;
      }
      const existing = byRow.get(index);
      if (existing === undefined) {
        byRow.set(index, [issue]);
      } else {
        existing.push(issue);
      }
    }
    return byRow;
  }, [refIssues, field.name]);

  if (datasetId === null) {
    return (
      <Text.Caption className="text-muted-foreground block">
        Choose a dataset first, then add filters on its columns.
      </Text.Caption>
    );
  }

  return (
    <div className="flex flex-col gap-2">
      {conditions.length === 0 ? (
        <Text.Caption className="text-muted-foreground">
          No filters — every row is returned.
        </Text.Caption>
      ) : (
        conditions.map((condition, index) => {
          // A saved column the dataset no longer has is still offered, so the
          // reason a filter stopped matching is visible rather than silently gone.
          const columnOptions =
            condition.column !== '' && !columns.includes(condition.column)
              ? [...columns, condition.column]
              : columns;
          return (
            <div
              key={index}
              className="border-border/60 flex flex-col gap-1.5 rounded-md border p-2"
            >
              <div className="flex items-center gap-2">
                <select
                  className={cn(SELECT_CLASS, 'min-w-0 flex-1')}
                  value={condition.column}
                  onChange={(event) =>
                    commit(
                      ConditionOps.setColumn(
                        conditions,
                        index,
                        event.target.value
                      )
                    )
                  }
                >
                  <option value="" disabled>
                    {shape === null ? 'Loading columns…' : 'Column…'}
                  </option>
                  {columnOptions.map((name) => (
                    <option key={name} value={name}>
                      {name}
                    </option>
                  ))}
                </select>
                <select
                  className={SELECT_CLASS}
                  value={condition.op}
                  onChange={(event) =>
                    commit(
                      ConditionOps.setOp(
                        conditions,
                        index,
                        event.target.value as DatasetFilterOp
                      )
                    )
                  }
                >
                  {DATASET_FILTER_OP_OPTIONS.map((option) => (
                    <option key={option.op} value={option.op}>
                      {option.label}
                    </option>
                  ))}
                </select>
                <button
                  type="button"
                  onClick={() =>
                    commit(ConditionOps.removeAt(conditions, index))
                  }
                  title="Remove filter"
                  aria-label={`Remove filter ${index + 1}`}
                  className="text-muted-foreground hover:bg-muted hover:text-foreground flex size-6 shrink-0 items-center justify-center rounded transition-colors"
                >
                  <X className="size-3.5" />
                </button>
              </div>
              {datasetFilterOpTakesValue(condition.op) ? (
                <TokenInput
                  value={condition.value}
                  placeholder="Value — type, or press / for a previous step"
                  sources={sources}
                  onChange={(template) =>
                    commit(ConditionOps.setValue(conditions, index, template))
                  }
                />
              ) : null}
              <RefIssueNotice issues={issuesByRow.get(index) ?? []} />
            </div>
          );
        })
      )}
      <Button
        type="button"
        variant="ghost"
        size="sm"
        className="h-7 self-start"
        onClick={() => commit(ConditionOps.add(conditions))}
      >
        <Plus className="mr-1 size-3.5" />
        Add a filter
      </Button>
    </div>
  );
};
